import { useState, type ReactElement } from "react";

import type { Photo, QuestionnaireOut, TrendPoint } from "../../api/types";
import { shortDate, utcDateOnly } from "../../lib/date";
import { median } from "../../lib/math";
import { TabPanel } from "../Tabs";
import { FolderPhoto } from "./PhotoLibrary";

// Daily log: the participant's diary — one collapsible FOLDER per logged day
// (same folder UI as the photo library) covering the 7-day hot window: the
// day's sleep / stress / activity factors, its FaceAge, and its photos. Days
// whose photos aged out to the archive are PRUNED here along with the photos
// (user decision 2026-08-07; 30→7 days 2026-08-18) — the dashboard shows the last 7 days; the
// Trends chart keeps the full FaceAge history. The day's FaceAge is the
// median of its TREND points (joined by questionnaire_response_id), so
// "pending" only ever means estimates are still coming.

interface DailyEntry {
  id: number;
  date: string;
  faceAge: number | null;
  sleepHours: number | null;
  sleepQuality: number | null;
  stress: number | null;
  activity: string | null;
  photos: Photo[];
}

function num(payload: Record<string, unknown>, key: string): number | null {
  const v = payload[key];
  return typeof v === "number" ? v : null;
}

function str(payload: Record<string, unknown>, key: string): string | null {
  const v = payload[key];
  return typeof v === "string" && v ? v : null;
}

function activityLabel(payload: Record<string, unknown>): string | null {
  const type = str(payload, "activity_type");
  if (!type || type === "none") return type === "none" ? "None" : null;
  const base = type === "other" ? (str(payload, "activity_other") ?? "Other") : type;
  const hours = num(payload, "activity_duration_hours");
  return hours != null ? `${base} · ${hours} h` : base;
}

function buildEntries(
  questionnaires: QuestionnaireOut[],
  photos: Photo[],
  trends: TrendPoint[],
): DailyEntry[] {
  const dailies = questionnaires.filter((q) => q.questionnaire_type === "daily");
  const entries = dailies.map((q) => {
    const linked = photos.filter((p) => p.questionnaire_response_id === q.id);
    const points = trends.filter((t) => t.questionnaire_response_id === q.id);
    const photoDate = linked.find((p) => p.taken_at)?.taken_at ?? points[0]?.captured_at ?? null;
    return {
      id: q.id,
      // Reduce to the UTC calendar date: photo timestamps are the chosen date
      // at UTC midnight, so formatting the full timestamp in local time would
      // shift the day back in negative timezones.
      date: utcDateOnly(photoDate ?? q.submitted_at),
      // Median of the day's estimates, from the TREND series — same number
      // the visible-photo median gave (median convention since 2026-07-30).
      faceAge: median(points.map((t) => t.estimated_age)),
      // Estimates exist but no visible photos: the day's files aged out of
      // the 7-day hot window into the archive — pruned below.
      archived: linked.length === 0 && points.length > 0,
      sleepHours: num(q.payload, "sleep_hours"),
      sleepQuality: num(q.payload, "sleep_quality"),
      stress: num(q.payload, "stress_level"),
      activity: activityLabel(q.payload),
      photos: linked,
    };
  });
  // The diary matches the photo hot window: archived days are pruned along
  // with their photos; the Trends chart keeps the full history. Newest first.
  return entries
    .filter((e) => !e.archived)
    .map(({ archived: _archived, ...entry }) => entry)
    .sort((a, b) => b.date.localeCompare(a.date));
}

function Rating({ label, value }: { label: string; value: number }): ReactElement {
  return (
    <div className="factor">
      <span className="factor-label">{label}</span>
      <span className="rating" aria-label={`${value} of 5`}>
        {[1, 2, 3, 4, 5].map((n) => (
          <i key={n} className={`rating-pip${n <= value ? " on" : ""}`} aria-hidden="true" />
        ))}
      </span>
    </div>
  );
}

function Factor({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div className="factor">
      <span className="factor-label">{label}</span>
      <span className="factor-value">{value}</span>
    </div>
  );
}

// Most recent daily folders shown at once; the rest defer behind "Show older"
// in same-size batches (~2 weeks of daily submissions per page).
const ENTRIES_PER_PAGE = 14;

export function DailyLog({
  photos,
  trends,
  questionnaires,
}: {
  photos: Photo[];
  trends: TrendPoint[];
  questionnaires: QuestionnaireOut[];
}): ReactElement {
  const entries = buildEntries(questionnaires, photos, trends);
  const [visibleCount, setVisibleCount] = useState(ENTRIES_PER_PAGE);
  // The newest day starts open so there's something to see on first view;
  // the rest stay collapsed (same behavior as the photo library folders).
  const [open, setOpen] = useState<Set<number>>(() => {
    const newest = entries[0]?.id;
    return newest != null ? new Set([newest]) : new Set();
  });
  const visibleEntries = entries.slice(0, visibleCount);
  const hiddenCount = entries.length - visibleEntries.length;

  function toggle(id: number): void {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <TabPanel id="daily" title="Daily Log">
      {entries.length === 0 ? (
        <p className="muted">
          Nothing logged yet. Add daily factors (sleep, stress, activity) when you upload photos to
          see how they line up with your FaceAge.
        </p>
      ) : (
        <>
          {/* Keep the "7 days" copy in sync with FACEAGE_ARCHIVE_HOT_DAYS. */}
          <p className="muted">
            Your log shows the last 7 days; older entries are archived securely by the study
            team, and the Trends tab keeps your full FaceAge history.
          </p>
          <ul className="folder-list daily-log">
              {visibleEntries.map((e) => {
                const isOpen = open.has(e.id);
                const panelId = `daily-folder-${e.id}`;
                return (
                  <li key={e.id} className="photo-folder">
                    <button
                      type="button"
                      className="folder-head"
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => toggle(e.id)}
                    >
                      <i
                        className={`fas fa-folder${isOpen ? "-open" : ""} folder-icon`}
                        aria-hidden="true"
                      />
                      <span className="folder-name">{shortDate(e.date)}</span>
                      {e.faceAge != null ? (
                        <span className="daily-faceage">FaceAge {e.faceAge}</span>
                      ) : e.photos.length > 0 ? (
                        // Hot photos, estimates not in yet — genuinely pending.
                        <span className="daily-faceage pending">FaceAge pending</span>
                      ) : null}
                      <span className="folder-count">
                        {e.photos.length} photo{e.photos.length === 1 ? "" : "s"}
                      </span>
                      <i
                        className={`fas fa-chevron-${isOpen ? "down" : "right"} folder-caret`}
                        aria-hidden="true"
                      />
                    </button>
                    {isOpen ? (
                      <div className="folder-body" id={panelId}>
                        <div className="daily-factors">
                          {e.sleepHours != null ? (
                            <Factor label="Sleep" value={`${e.sleepHours} h`} />
                          ) : null}
                          {e.sleepQuality != null ? (
                            <Rating label="Sleep quality" value={e.sleepQuality} />
                          ) : null}
                          {e.stress != null ? <Rating label="Stress" value={e.stress} /> : null}
                          {e.activity ? <Factor label="Activity" value={e.activity} /> : null}
                        </div>
                        {e.photos.length > 0 ? (
                          <div className="folder-grid">
                            {e.photos.map((p) => (
                              <FolderPhoto
                                key={p.id}
                                photo={p}
                                alt={`Photo from ${shortDate(e.date)}`}
                                resultsVisible
                              />
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          {hiddenCount > 0 ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setVisibleCount((n) => n + ENTRIES_PER_PAGE)}
            >
              Show older ({hiddenCount} more)
            </button>
          ) : null}
        </>
      )}
    </TabPanel>
  );
}
